import React, { useState } from 'react';
import { toast } from 'sonner';
import type { SimulationSession } from '../../sim-onboarding/types';

//#region AC_BLOCK session-card -- Card de sessão de simulação com controles lifecycle
// @ac-purpose Exibe status, município, escolas e ações (iniciar/pausar/retomar/encerrar) de uma sessão
// @ac-related react-app/src/features/developer-cockpit/stores/useSessionsSSE.ts#use-sessions-sse

export interface SessionCardProps {
  session: SimulationSession;
  selected?: boolean;
  onSelect?: (session: SimulationSession) => void;
  onIniciar:  (id: string) => Promise<void>;
  onPausar:   (id: string) => Promise<void>;
  onRetomar:  (id: string) => Promise<void>;
  onEncerrar: (id: string) => Promise<void>;
}

const STATUS_CONFIG: Record<SimulationSession['status'], { label: string; color: string; dot: string; bg: string }> = {
  PREPARING: { label: 'Preparando',  color: '#fbbf24', dot: '#fbbf24', bg: 'rgba(251,191,36,0.08)' },
  RUNNING:   { label: 'Em execução', color: '#4ade80', dot: '#4ade80', bg: 'rgba(74,222,128,0.08)'  },
  PAUSED:    { label: 'Pausada',     color: '#60a5fa', dot: '#60a5fa', bg: 'rgba(96,165,250,0.08)'  },
  STOPPED:   { label: 'Encerrada',   color: '#64748b', dot: '#64748b', bg: 'rgba(100,116,139,0.06)' },
  ERROR:     { label: 'Erro',        color: '#f87171', dot: '#f87171', bg: 'rgba(239,68,68,0.08)'   },
};

function elapsed(from: string | null): string {
  if (!from) return '—';
  const ms = Date.now() - new Date(from).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function SessionCard({
  session, selected, onSelect, onIniciar, onPausar, onRetomar, onEncerrar,
}: SessionCardProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const cfg = STATUS_CONFIG[session.status] ?? STATUS_CONFIG.ERROR;

  const act = async (fn: () => Promise<void>, label: string) => {
    setLoading(label);
    try {
      await fn();
    } catch (e: any) {
      const raw = String(e?.message || e || 'Falha na ação da sessão');
      let msg = raw;
      try {
        const parsed = JSON.parse(raw);
        msg = parsed?.message || parsed?.error || raw;
        if (Array.isArray(msg)) msg = msg.join(' · ');
      } catch { /* texto puro */ }
      toast.error(msg);
    }
    setLoading(null);
  };

  const isActive = session.status === 'RUNNING' || session.status === 'PAUSED' || session.status === 'PREPARING';

  return (
    <div
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onClick={() => onSelect?.(session)}
      onKeyDown={(e) => {
        if (!onSelect) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(session);
        }
      }}
      style={{
        borderRadius: 16,
        border: selected ? `1.5px solid ${cfg.color}` : `1px solid ${cfg.color}33`,
        background: selected ? `${cfg.bg}` : cfg.bg,
        boxShadow: selected ? `0 0 0 1px ${cfg.color}55, 0 8px 24px rgba(0,0,0,0.35)` : 'none',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        cursor: onSelect ? 'pointer' : 'default',
      }}
    >
      {/* Glow lateral de status */}
      <div style={{ position:'absolute', left:0, top:'10%', bottom:'10%', width:3, borderRadius:'0 3px 3px 0', background: cfg.color, opacity: 0.9 }} />

      {selected && (
        <div style={{
          position: 'absolute', top: 8, right: 10,
          fontSize: 8, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
          color: cfg.color, opacity: 0.95,
        }}>
          Em foco · telemetria
        </div>
      )}

      {/* Header */}
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:4 }}>
            {/* Dot animado para RUNNING */}
            <div style={{
              width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flexShrink: 0,
              boxShadow: session.status === 'RUNNING' ? `0 0 8px ${cfg.dot}` : 'none',
              animation: session.status === 'RUNNING' ? 'pulse 2s ease-in-out infinite' : 'none',
            }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {cfg.label}
            </span>
            <span style={{ fontSize: 10, color: '#475569', marginLeft: 4 }}>
              {session.status === 'RUNNING' && session.iniciadoEm && `↑ ${elapsed(session.iniciadoEm)}`}
              {session.status === 'PAUSED'  && session.pausadoEm  && `⏸ ${elapsed(session.pausadoEm)}`}
            </span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-0.01em' }}>
            {session.municipioNome}
            <span style={{ fontSize: 12, fontWeight: 400, color: '#64748b', marginLeft: 6 }}>— {session.municipioUf}</span>
          </div>
          <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>
            {session.escolaIds.length} escola(s) · {session.actorIds.length} ator(es)
          </div>
        </div>

        {/* ID curto */}
        <div style={{ fontSize: 9, color: '#334155', fontFamily: 'monospace', flexShrink: 0 }}>
          #{session.id.slice(0, 8)}
        </div>
      </div>

      {/* Pills de escolas (máx 3 visíveis) */}
      {session.escolaIds.length > 0 && (
        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
          {session.escolaIds.slice(0, 3).map(id => (
            <span key={id} style={{ fontSize:9, padding:'2px 7px', borderRadius:99, background:'rgba(124,58,237,0.1)', border:'1px solid rgba(124,58,237,0.2)', color:'#a78bfa', fontFamily:'monospace' }}>
              {id.slice(0, 8)}…
            </span>
          ))}
          {session.escolaIds.length > 3 && (
            <span style={{ fontSize:9, padding:'2px 7px', borderRadius:99, background:'rgba(255,255,255,0.04)', color:'#64748b' }}>
              +{session.escolaIds.length - 3} mais
            </span>
          )}
        </div>
      )}

      {/* Ações */}
      {session.status !== 'STOPPED' && (
        <div style={{ display:'flex', gap:6, marginTop:2 }} onClick={(e) => e.stopPropagation()}>
          {session.status === 'PREPARING' && (
            <ActionBtn
              label={loading === 'iniciar' ? '...' : '▶ Iniciar'}
              color="#4ade80" bg="rgba(74,222,128,0.12)"
              disabled={!!loading}
              onClick={() => act(() => onIniciar(session.id), 'iniciar')}
            />
          )}
          {session.status === 'RUNNING' && (
            <ActionBtn
              label={loading === 'pausar' ? '...' : '⏸ Pausar'}
              color="#60a5fa" bg="rgba(96,165,250,0.12)"
              disabled={!!loading}
              onClick={() => act(() => onPausar(session.id), 'pausar')}
            />
          )}
          {session.status === 'PAUSED' && (
            <ActionBtn
              label={loading === 'retomar' ? '...' : '▶ Retomar'}
              color="#4ade80" bg="rgba(74,222,128,0.12)"
              disabled={!!loading}
              onClick={() => act(() => onRetomar(session.id), 'retomar')}
            />
          )}
          {isActive && (
            <ActionBtn
              label={loading === 'encerrar' ? '...' : '■ Encerrar'}
              color="#f87171" bg="rgba(239,68,68,0.1)"
              disabled={!!loading}
              onClick={() => {
                if (window.confirm(`Encerrar simulação de ${session.municipioNome}?`)) {
                  act(() => onEncerrar(session.id), 'encerrar');
                }
              }}
            />
          )}
        </div>
      )}

      {session.status === 'STOPPED' && (
        <div style={{ fontSize:11, color:'#334155' }}>
          Encerrada em {session.encerradoEm ? new Date(session.encerradoEm).toLocaleString('pt-BR') : '—'}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ label, color, bg, disabled, onClick }: {
  label: string; color: string; bg: string; disabled: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '6px 14px', borderRadius: 8, border: `1px solid ${color}44`,
        background: disabled ? 'rgba(255,255,255,0.03)' : bg,
        color: disabled ? '#334155' : color,
        fontSize: 11, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.15s ease',
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = 'brightness(1.2)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
    >
      {label}
    </button>
  );
}
//#endregion AC_BLOCK session-card
